import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { useCookies } from "react-cookie";

// bootstrap imports
import Container from "react-bootstrap/Container";

import { Button, Badge, Form } from "react-bootstrap";

import { Pagination } from "react-bootstrap";

// app-specific
import Table from "../components/Table";
import Modal from "react-bootstrap/Modal";
import Spinner from "react-bootstrap/Spinner";
import NavBar from "../components/NavBar";
import { NavigateRoutes } from "../constants/routes";

import {
  IoInformationCircleOutline,
  IoAddCircle,
  IoTrash,
  IoTime,
  IoCloseCircle,
  IoPersonRemove,
} from "react-icons/io5";

// utils
import { tokenIsValid, decode, getTokenExpirationDate } from "../utils/token";

import {
  getAllEvents,
  sendConfirmedTraining,
  deleteEvent,
  cancelTraining,
  rescheduleTraining,
  markTrainingNoShow,
  addParticipant,
  searchUsers,
} from "../api/Hub.js";

// custom page styling
import styles from "../styles/Admin.module.css";

// events cancelled from this page keep their record and carry this status;
// everything scheduled before the feature existed has none
const CANCELLED_STATUS = "cancelled";
const NO_SHOW_STATUS = "no_show";
const RESCHEDULE_UNCHANGED = "Change the date or time to reschedule.";

// events are stored as "4/15/2026" and "09:00 am", while the date and time
// inputs need "2026-04-15" and "09:00"
const toDateInputValue = (dayToMeet) => {
  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec((dayToMeet || "").trim());
  if (!match) return "";
  const [, month, day, year] = match;
  return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
};

const toTimeInputValue = (timeToMeet) => {
  const match = /^(\d{1,2}):(\d{2})\s*([ap])\.?m\.?$/i.exec((timeToMeet || "").trim());
  if (!match) return "";
  let hour = Number(match[1]) % 12;
  if (match[3].toLowerCase() === "p") hour += 12;
  return `${String(hour).padStart(2, "0")}:${match[2]}`;
};

// Event times are entered and shown in Central time, but the browser reads
// them as its own local time. That is exact for anyone working in Central time
// and off by the time zone difference for anyone who is not.
const hasStarted = (eventDetails) => {
  const date = toDateInputValue(eventDetails.day_to_meet);
  const time = toTimeInputValue(eventDetails.time_to_meet);
  if (!date || !time) return false;
  const start = new Date(`${date}T${time}`);
  return !Number.isNaN(start.getTime()) && start < new Date();
};

// how many earlier dates for this event the participant missed
const missedBefore = (eventDetails) =>
  (eventDetails.reschedule_history || []).filter(
    (entry) => entry.previous_status === NO_SHOW_STATUS,
  ).length;

/**
 * Page component for displaying the Admin page
 * @returns React.FC
 */
const ExistingTrainingPage = () => {
  // state - confirm logs scheduler
  const [confirmedTraining, setConfirmedTraining] = useState([]);
  const [confirmedtrainingCurPage, setConfirmedTrainingCurPage] = useState(1);
  const [confirmedTrainingTotalPage, setConfirmedTrainingTotalPage] =
    useState();
  const [confirmedTrainingFilters, setConfirmedTrainingFilters] = useState([]);

  // New state for client-side caching with lazy loading
  const [allEventsCache, setAllEventsCache] = useState([]);
  const [lastRefreshTime, setLastRefreshTime] = useState(null);
  const [isRefreshingCache, setIsRefreshingCache] = useState(false);
  const [hasMoreEvents, setHasMoreEvents] = useState(false);
  const [eventsOffset, setEventsOffset] = useState(0);
  const [isFetchingMore, setIsFetchingMore] = useState(false);

  // state - get user info
  const [userData, setUserData] = useState();

  // state - track appropriate variables for whether the user is able to access this page
  const [authorized, setAuthorized] = useState();
  const [reauthorized, setReauthorized] = useState(false);

  // Modal information
  const [modalShow, setModalShow] = useState(false);
  const [modalHeader, setModalHeader] = useState("");
  const [modalBtnText, setModalBtnText] = useState("");
  const [modalBody, setModalBody] = useState("");
  const [showModalClose, setShowModalClose] = useState(false);

  const [infoToUse, setInfoToUse] = useState([]);

  // Delete confirmation state
  const [deleteConfirmShow, setDeleteConfirmShow] = useState(false);
  const [deleteEventData, setDeleteEventData] = useState(null);
  const [isDeletingEvent, setIsDeletingEvent] = useState(false);

  // Cancel confirmation state
  const [cancelConfirmShow, setCancelConfirmShow] = useState(false);
  const [cancelEventData, setCancelEventData] = useState(null);
  const [isCancellingEvent, setIsCancellingEvent] = useState(false);
  const [cancelError, setCancelError] = useState("");

  // No show state
  const [noShowConfirmShow, setNoShowConfirmShow] = useState(false);
  const [noShowEventData, setNoShowEventData] = useState(null);
  const [isMarkingNoShow, setIsMarkingNoShow] = useState(false);
  const [noShowError, setNoShowError] = useState("");

  // Reschedule state
  const [rescheduleShow, setRescheduleShow] = useState(false);
  const [rescheduleEventData, setRescheduleEventData] = useState(null);
  const [newEventDate, setNewEventDate] = useState("");
  const [newEventTime, setNewEventTime] = useState("");
  const [isReschedulingEvent, setIsReschedulingEvent] = useState(false);
  const [rescheduleError, setRescheduleError] = useState("");

  // Add participant state
  const [addParticipantShow, setAddParticipantShow] = useState(false);
  const [selectedEventForAdd, setSelectedEventForAdd] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [addParticipantLoading, setAddParticipantLoading] = useState(false);

  // navigate hook
  const navigate = useNavigate();

  // cookies hook
  const [cookies] = useCookies(["token", "firebaseToken"]);

  // Initial authorization check
  useEffect(() => {
    const token = cookies["token"];

    if (token && tokenIsValid(token)) {
      const decoded = decode(token);

      if (decoded) {
        setUserData(decoded);
        setAuthorized(true);
        setReauthorized(true);
      }
    }
  }, [cookies]);

  const handleSendConfirmedTraining = () => {
    console.log("BUTTON PRESSED");
    setShowModalClose(false);

    sendConfirmedTraining(
      userData,
      infoToUse.event_details.training,
      infoToUse.event_details.eventID,
      infoToUse.event_details.source,
    )
      .then((res) => {
        // Check if response is an error object (new format) or old numeric format
        const isErrorResponse =
          typeof res.data === "object" &&
          res.data !== null &&
          res.data.error === true;
        const isOldNumericError = typeof res.data === "number" && res.data < 0;

        if (isErrorResponse || isOldNumericError) {
          setShowModalClose(false);

          if (isErrorResponse) {
            // New error format with detailed information
            const errorData = res.data;

            // Determine user-friendly message based on error type
            let userMessage = "";
            if (errorData.type === "duplicate_event") {
              userMessage = "You're already registered for this event.";
            } else if (errorData.type === "trainer_conflict") {
              userMessage =
                "The trainer has a scheduling conflict. Each training blocks a 2-hour window.";
            } else if (errorData.type === "trainee_conflict") {
              userMessage =
                "You have a scheduling conflict. Each training blocks a 2-hour window.";
            } else {
              userMessage =
                "There was a scheduling conflict. Please select a different time.";
            }

            setModalHeader(
              `Error: ${errorData.type?.replace(/_/g, " ").toUpperCase()}`,
            );

            let diagnosticInfo = `<b>Details:</b><br/>`;
            diagnosticInfo += `Trainee: ${errorData.user_email}<br/>`;
            diagnosticInfo += `Trainer: ${errorData.trainer_email}<br/>`;
            diagnosticInfo += `Training: ${errorData.training_type}<br/>`;
            diagnosticInfo += `Date: ${errorData.requested_date} at ${errorData.requested_time}`;

            setModalBody(
              <Container>
                <p>{userMessage}</p>
                <hr />
                <p dangerouslySetInnerHTML={{ __html: diagnosticInfo }} />
              </Container>,
            );
          } else if (res.data === -1) {
            // Old format fallback for trainer conflict
            setModalHeader("Trainer Time Conflict");
            setModalBody(
              <Container>
                <p>
                  There was an issue with creating the event. The trainer
                  already has a time conflict with the current time selected.
                  Please select a different time.
                </p>
              </Container>,
            );
          } else if (res.data === -2) {
            // Old format fallback for trainee conflict
            setModalHeader("Your Time Conflict");
            setModalBody(
              <Container>
                <p>
                  There was an issue with creating the event. You already have a
                  time conflict with the current time selected. Please select a
                  different time.
                </p>
              </Container>,
            );
          } else {
            setModalHeader("Error");
            setModalBody(
              <Container>
                <p>
                  There was an issue with creating the event. Please check with
                  the respective scheduler to confirm. Error code: {res.data}
                </p>
              </Container>,
            );
          }
          setModalShow(true);
        } else {
          setModalHeader("Success");
          setModalBody(
            <Container>
              <p>Successfully joined the event!</p>
              <p>
                You will receive confirmation emails shortly. This page will
                redirect to the home screen in three (3) seconds.
              </p>
            </Container>,
          );
          setTimeout(redirectHome, 3000);
          setModalShow(true);
          setShowModalClose(false);
          // Auto-refresh the list
          setTimeout(() => {
            fetchAndCacheAllEvents();
          }, 500);
        }
      })
      .catch((err) => {
        console.log("Error joining training:", err);
        setModalHeader("Error");
        setModalBody(
          <Container>
            <p>
              Failed to join training:{" "}
              {err.response?.data?.detail || err.message || "Unknown error"}
            </p>
          </Container>,
        );
        setModalShow(true);
        setShowModalClose(false);
      });

    setModalHeader("Event Pending");
    setModalBody(
      <Container>
        <p>
          Training Event has been submitted please wait for this screen to
          update to confirm the event creation.
        </p>
      </Container>,
    );
    setModalShow(true);
    setShowModalClose(false);
  };

  const attachToTraining = (info) => {
    setInfoToUse(info);

    const newModalBody = [
      <h5 key="modal-header" style={{ textDecorationLine: "underline" }}>
        Are you sure you want to join the following event?
      </h5>,
    ];

    newModalBody.push(
      <h5 key="Trainer_Info">
        <b>Trainer Information</b>
      </h5>,
    );

    newModalBody.push(
      <table>
        <tr>
          <td>
            <b>Trainer Name:&nbsp;</b>
          </td>
          <td>{info.trainer.name}</td>
        </tr>
        <tr>
          <td>
            <b>Trainer Email:&nbsp;</b>
          </td>
          <td>{info.trainer.email}</td>
        </tr>
        <tr>
          <td>
            <b>Trainer Phone Number:&nbsp;</b>
          </td>
          <td>{info.trainer.phone}</td>
        </tr>
        <tr>
          <td>
            <b>Trainer Zoom ID:&nbsp;</b>
          </td>
          <td>{info.trainer.zoom_id}</td>
        </tr>
      </table>,
    );

    newModalBody.push(<br></br>);
    newModalBody.push(
      <h5 key="Event_Info">
        <b>Event Information</b>
      </h5>,
    );

    newModalBody.push(
      <table>
        <tr>
          <td>
            <b>Event Type: </b>
          </td>
          <td>{info.event_details.training}</td>
        </tr>
        <tr>
          <td>
            <b>Day to Meet: </b>
          </td>
          <td>{info.event_details.day_to_meet}</td>
        </tr>
        <tr>
          <td>
            <b>Time to Meet (in CST):&nbsp;</b>
          </td>
          <td>{info.event_details.time_to_meet}</td>
        </tr>
        <tr>
          <td>
            <b>Event ID:&nbsp;</b>
          </td>
          <td>{info.event_details.eventID}</td>
        </tr>
      </table>,
    );

    // Add warning note if this is an exam
    if (
      info.event_details.training &&
      info.event_details.training.toLowerCase().includes("exam")
    ) {
      newModalBody.push(<br key="exam-warning-br" />);
      newModalBody.push(
        <div
          key="exam-warning"
          style={{
            backgroundColor: "#fff3cd",
            border: "1px solid #ffc107",
            borderRadius: "4px",
            padding: "10px",
            marginTop: "10px",
          }}
        >
          <strong>NOTE:</strong> You are joining an <strong>exam</strong>.
          Please make sure you are prepared and have completed all required
          trainings.
        </div>,
      );
    }

    setModalHeader("Final Confirmation More Info");
    setModalBody(newModalBody);
    setModalBtnText("Confirm");
    setModalShow(true);
    setShowModalClose(true);
  };

  const infoConfirmedTraining = (info) => {
    console.log("IN INFO CONFIRM TRAINING");
    console.log(info);

    const newModalBody = [
      <h5 key="modal-header" style={{ textDecorationLine: "underline" }}>
        More Information:
      </h5>,
    ];

    newModalBody.push(
      <h5 key="Trainer_Info">
        <b>Trainer Information</b>
      </h5>,
    );

    newModalBody.push(
      <table>
        <tr>
          <td>
            <b>Trainer Name:&nbsp;</b>
          </td>
          <td>{info.trainer.name}</td>
        </tr>
        <tr>
          <td>
            <b>Trainer Email:&nbsp;</b>
          </td>
          <td>{info.trainer.email}</td>
        </tr>
        <tr>
          <td>
            <b>Trainer Phone Number:&nbsp;</b>
          </td>
          <td>{info.trainer.phone}</td>
        </tr>
        <tr>
          <td>
            <b>Trainer Zoom ID:&nbsp;</b>
          </td>
          <td>{info.trainer.zoom_id}</td>
        </tr>
      </table>,
    );

    newModalBody.push(<br></br>);

    // Add Trainee/Participant Information
    if (info.trainee) {
      newModalBody.push(
        <h5 key="Trainee_Info">
          <b>Participant Information</b>
        </h5>,
      );

      const trainee = info.trainee;
      newModalBody.push(
        <table>
          {trainee.name && (
            <tr>
              <td>
                <b>Name:&nbsp;</b>
              </td>
              <td>{trainee.name}</td>
            </tr>
          )}
          {trainee.email && (
            <tr>
              <td>
                <b>Email:&nbsp;</b>
              </td>
              <td>{trainee.email}</td>
            </tr>
          )}
          {trainee.phone && (
            <tr>
              <td>
                <b>Phone Number:&nbsp;</b>
              </td>
              <td>{trainee.phone}</td>
            </tr>
          )}
        </table>,
      );
      newModalBody.push(<br></br>);
    }

    newModalBody.push(
      <h5 key="Event_Info">
        <b>Event Information</b>
      </h5>,
    );

    newModalBody.push(
      <table>
        <tr>
          <td>
            <b>Event Type: </b>
          </td>
          <td>{info.event_details.training}</td>
        </tr>
        <tr>
          <td>
            <b>Day to Meet: </b>
          </td>
          <td>{info.event_details.day_to_meet}</td>
        </tr>
        <tr>
          <td>
            <b>Time to Meet (in CST):&nbsp;</b>
          </td>
          <td>{info.event_details.time_to_meet}</td>
        </tr>
        <tr>
          <td>
            <b>Event ID:&nbsp;</b>
          </td>
          <td>{info.event_details.eventID}</td>
        </tr>
      </table>,
    );

    if (info.event_details.status === CANCELLED_STATUS) {
      newModalBody.push(
        <p key="Cancelled_Note">
          <b>This event has been cancelled.</b>
        </p>,
      );
    }

    if (info.event_details.status === NO_SHOW_STATUS) {
      newModalBody.push(
        <p key="No_Show_Note">
          <b>The participant did not show up for this event.</b>
          <br />
          Rescheduling it books a second attempt and clears the no show.
        </p>,
      );
    }

    const missed = missedBefore(info.event_details);
    if (missed > 0 && info.event_details.status !== NO_SHOW_STATUS) {
      newModalBody.push(
        <p key="Previous_No_Show">
          <b>
            Previously a no show{missed > 1 ? ` (${missed} times)` : ""}
          </b>
          <br />
          The dates the participant missed are marked below.
        </p>,
      );
    }

    if ((info.event_details.reschedule_history || []).length > 0) {
      newModalBody.push(
        <p key="Reschedule_History">
          <b>Previously scheduled for</b>
          {info.event_details.reschedule_history.map((entry, idx) => (
            <React.Fragment key={idx}>
              <br />
              {entry.previous_day_to_meet} at {entry.previous_time_to_meet}
              {entry.previous_status === NO_SHOW_STATUS && <> (no show)</>}
            </React.Fragment>
          ))}
        </p>,
      );
    }

    setModalHeader("Event Details");
    setModalBody(newModalBody);
    setModalShow(true);
  };

  // alias for redirect back to login page
  const redirectLogin = useCallback(
    () => navigate(NavigateRoutes.LOGIN + "?redirect=HOME"),
    [navigate],
  );

  const redirectHome = useCallback(
    () => navigate(NavigateRoutes.HOME),
    [navigate],
  );

  // Helper: Fetch events batch and cache them (lazy loading with pagination)
  const fetchAndCacheAllEvents = useCallback(() => {
    setIsRefreshingCache(true);
    // Load first batch: 50 events (5 pages × 10 per page)
    return getAllEvents([], 50, 0)
      .then((res) => {
        console.log("getAllEvents response:", res);
        console.log("Response data:", res.data);
        if (res.data.success) {
          console.log("Events received:", res.data.data.length);
          console.log("Total available:", res.data.total);
          console.log("Has more:", res.data.has_more);
          setAllEventsCache(res.data.data);
          setHasMoreEvents(res.data.has_more);
          setEventsOffset(50); // Next batch starts at offset 50
          setLastRefreshTime(new Date());
        } else {
          console.error("API returned success=false:", res.data);
        }
        setIsRefreshingCache(false);
      })
      .catch((err) => {
        console.error("Error fetching events:", err);
        setIsRefreshingCache(false);
      });
  }, []);

  // Helper: Load more events when user navigates to pages beyond what's loaded
  const loadMoreEvents = useCallback(() => {
    if (isFetchingMore || !hasMoreEvents) return;

    setIsFetchingMore(true);
    // Load next batch of 50 events
    getAllEvents([], 50, eventsOffset)
      .then((res) => {
        if (res.data.success) {
          console.log("Loaded more events:", res.data.data.length);
          setAllEventsCache((prevCache) => [...prevCache, ...res.data.data]);
          setHasMoreEvents(res.data.has_more);
          setEventsOffset(eventsOffset + 50);
        }
        setIsFetchingMore(false);
      })
      .catch((err) => {
        console.error("Error loading more events:", err);
        setIsFetchingMore(false);
      });
  }, [eventsOffset, hasMoreEvents, isFetchingMore]);

  // Fetch all events when user is reauthorized
  useEffect(() => {
    if (reauthorized) {
      fetchAndCacheAllEvents();
    }
  }, [reauthorized, fetchAndCacheAllEvents]);

  // Helper: Filter cached events based on selected event types
  const getFilteredEvents = useCallback(() => {
    if (confirmedTrainingFilters.length === 0) {
      // "All" is selected or no filters applied - return all events
      return allEventsCache;
    }
    // Filter by selected event types
    return allEventsCache.filter((event) =>
      confirmedTrainingFilters.includes(event.event_details.event_type),
    );
  }, [allEventsCache, confirmedTrainingFilters]);

  // Helper: Get paginated slice from FILTERED cache
  const getPaginatedFilteredData = useCallback(
    (page, pageSize) => {
      const filteredData = getFilteredEvents();
      if (filteredData.length === 0) return { data: [], totalPage: 0 };

      const totalPages = Math.ceil(filteredData.length / pageSize);

      if (page > totalPages) {
        return { data: [], totalPage: totalPages };
      }

      const startIdx = (page - 1) * pageSize;
      const endIdx = startIdx + pageSize;

      return {
        data: filteredData.slice(startIdx, endIdx),
        totalPage: totalPages,
      };
    },
    [getFilteredEvents],
  );

  // Update displayed data based on current page and filters
  useEffect(() => {
    if (reauthorized && allEventsCache.length > 0) {
      const paginatedResult = getPaginatedFilteredData(
        confirmedtrainingCurPage,
        10,
      );
      setConfirmedTraining(paginatedResult.data);
      setConfirmedTrainingTotalPage(paginatedResult.totalPage);
    }
  }, [
    reauthorized,
    allEventsCache,
    confirmedtrainingCurPage,
    getPaginatedFilteredData,
  ]);

  // Auto-refresh cache every 30 seconds
  useEffect(() => {
    if (reauthorized) {
      // Set up auto-refresh timer
      const timer = setInterval(() => {
        fetchAndCacheAllEvents();
      }, 300000); // 5 minutes
      return () => clearInterval(timer);
    }
  }, [reauthorized, fetchAndCacheAllEvents]);

  useEffect(() => {
    const exp = getTokenExpirationDate(cookies["token"]);
    if (exp) {
      const now = new Date();
      const newTimeout = setTimeout(
        () => setAuthorized(false),
        exp - now - 10 * 1000,
      );
      return () => clearTimeout(newTimeout);
    }
  }, [cookies]);

  // Redirect to login when unauthorized
  useEffect(() => {
    if (authorized === false) {
      console.log("REDIRECT LOGIN, authorized value:");
      console.log(authorized);
      const timeout = setTimeout(() => redirectLogin(), 5000);
      return () => clearTimeout(timeout);
    }
  }, [authorized, redirectLogin]);

  // =================================
  // -- Button handlers --
  // =================================

  const handleDeleteClick = (record) => {
    setDeleteEventData(record);
    setDeleteConfirmShow(true);
  };

  const confirmDelete = () => {
    if (!deleteEventData) return;

    setIsDeletingEvent(true);

    deleteEvent(
      deleteEventData.event_details.eventID,
      deleteEventData.event_details.source,
      cookies["token"],
    )
      .then((res) => {
        setDeleteConfirmShow(false);
        setIsDeletingEvent(false);
        if (res.data.success) {
          setModalHeader("Success");
          setModalBody(<p>Event deleted successfully.</p>);
          setModalShow(true);
          setShowModalClose(false);
          // After 1.5 seconds, show refreshing message and fetch
          setTimeout(() => {
            setModalBody(
              <div>
                <p>Event deleted successfully.</p>
                <p style={{ color: "#666", marginTop: "10px" }}>
                  Refreshing table for changes...
                </p>
              </div>,
            );
            fetchAndCacheAllEvents().then(() => {
              setDeleteEventData(null);
              setTimeout(() => {
                setModalShow(false);
              }, 1000);
            });
          }, 500);
        } else {
          setModalHeader("Error");
          setModalBody(<p>{res.data.message || "Failed to delete event."}</p>);
          setModalShow(true);
          setShowModalClose(false);
          setDeleteEventData(null);
        }
      })
      .catch((err) => {
        console.log(err);
        setDeleteConfirmShow(false);
        setDeleteEventData(null);
        setIsDeletingEvent(false);
        setModalHeader("Error");
        setModalBody(<p>Failed to delete event: {err.message}</p>);
        setModalShow(true);
        setShowModalClose(false);
      });
  };

  const handleAddParticipantClick = (record) => {
    if (!userData.is_admin) return;
    setSelectedEventForAdd(record);
    setSearchQuery("");
    setSearchResults([]);
    setSelectedUser(null);
    setAddParticipantShow(true);
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    searchUsers(query)
      .then((res) => {
        if (res.data.success) {
          setSearchResults(res.data.users);
        } else {
          setSearchResults([]);
        }
      })
      .catch((err) => {
        console.log(err);
        setSearchResults([]);
      });
  };

  const confirmAddParticipant = () => {
    if (!selectedUser || !selectedEventForAdd) return;

    setAddParticipantLoading(true);
    addParticipant(
      selectedEventForAdd.event_details.eventID,
      selectedEventForAdd.event_details.source,
      selectedUser.email,
      cookies["token"],
    )
      .then((res) => {
        setAddParticipantLoading(false);
        setAddParticipantShow(false);
        if (res.data.success) {
          setModalHeader("Success");
          setModalBody(<p>{res.data.message}</p>);
          setModalShow(true);
          setShowModalClose(false);
          // After 1.5 seconds, show refreshing message and fetch
          setTimeout(() => {
            setModalBody(
              <div>
                <p>{res.data.message}</p>
                <p style={{ color: "#666", marginTop: "10px" }}>
                  Refreshing table for changes...
                </p>
              </div>,
            );
            fetchAndCacheAllEvents().then(() => {
              setSelectedUser(null);
              setSearchQuery("");
              setSearchResults([]);
              setTimeout(() => {
                setModalShow(false);
              }, 1000);
            });
          }, 500);
        } else {
          setModalHeader("Error");
          setModalBody(
            <p>{res.data.message || "Failed to add participant."}</p>,
          );
          setModalShow(true);
          setShowModalClose(false);
        }
      })
      .catch((err) => {
        console.log(err);
        setAddParticipantLoading(false);
        setModalHeader("Error");
        setModalBody(<p>Failed to add participant: {err.message}</p>);
        setModalShow(true);
        setShowModalClose(false);
      });
  };

  const getEventTypeBadge = (eventType) => {
    if (eventType === "Exam") {
      return (
        <Badge bg="warning" text="dark">
          {eventType}
        </Badge>
      );
    } else if (eventType === "Mock") {
      return <Badge bg="info">{eventType}</Badge>;
    } else {
      return <Badge bg="success">{eventType}</Badge>;
    }
  };

  // =================================
  // -- Cancelling and rescheduling --
  // =================================

  const isCancelledEvent = (record) =>
    record.event_details.status === CANCELLED_STATUS;

  const isNoShowEvent = (record) =>
    record.event_details.status === NO_SHOW_STATUS;

  // an event that is not going ahead as it stands
  const isSettledEvent = (record) =>
    isCancelledEvent(record) || isNoShowEvent(record);

  const handleNoShowClick = (record) => {
    setNoShowEventData(record);
    setNoShowError("");
    setNoShowConfirmShow(true);
  };

  const closeNoShow = () => {
    // once the emails are on their way, let the request finish
    if (isMarkingNoShow) return;
    setNoShowConfirmShow(false);
    setNoShowEventData(null);
    setNoShowError("");
  };

  const confirmNoShow = () => {
    if (!noShowEventData || isMarkingNoShow) return;
    const record = noShowEventData;

    setIsMarkingNoShow(true);
    setNoShowError("");
    markTrainingNoShow(
      record.event_details.eventID,
      record.event_details.source,
      cookies["token"],
    )
      .then((res) => {
        setIsMarkingNoShow(false);
        if (!res.data.success) {
          setNoShowError(
            res.data.message || "Could not mark this event as a no show.",
          );
          return;
        }
        setNoShowConfirmShow(false);
        setNoShowEventData(null);
        setModalHeader("Marked as No Show");
        setModalBody(
          <div>
            <p>
              {record.trainee?.name || "The participant"} did not show up for{" "}
              {record.event_details.training} on{" "}
              {record.event_details.day_to_meet} at{" "}
              {record.event_details.time_to_meet}. Everyone on that event has
              been emailed.
            </p>
          </div>,
        );
        setShowModalClose(true);
        setModalShow(true);
        fetchAndCacheAllEvents();
      })
      .catch((err) => {
        console.error("Error marking no show:", err);
        setIsMarkingNoShow(false);
        setNoShowError(
          err.response?.data?.message || err.message || "Unknown error",
        );
      });
  };

  const handleCancelClick = (record) => {
    setCancelEventData(record);
    setCancelError("");
    setCancelConfirmShow(true);
  };

  const closeCancel = () => {
    // once the emails are on their way, let the request finish
    if (isCancellingEvent) return;
    setCancelConfirmShow(false);
    setCancelEventData(null);
    setCancelError("");
  };

  const confirmCancel = () => {
    if (!cancelEventData || isCancellingEvent) return;
    const record = cancelEventData;

    setIsCancellingEvent(true);
    setCancelError("");
    cancelTraining(
      record.event_details.eventID,
      record.event_details.source,
      cookies["token"],
    )
      .then((res) => {
        setIsCancellingEvent(false);
        if (!res.data.success) {
          setCancelError(res.data.message || "Could not cancel this event.");
          return;
        }
        setCancelConfirmShow(false);
        setCancelEventData(null);
        setModalHeader("Event Cancelled");
        setModalBody(
          <div>
            <p>
              {record.event_details.training} on{" "}
              {record.event_details.day_to_meet} at{" "}
              {record.event_details.time_to_meet} is cancelled, and everyone
              involved has been emailed.
            </p>
          </div>,
        );
        setShowModalClose(true);
        setModalShow(true);
        fetchAndCacheAllEvents();
      })
      .catch((err) => {
        console.error("Error cancelling event:", err);
        setIsCancellingEvent(false);
        setCancelError(
          err.response?.data?.message || err.message || "Unknown error",
        );
      });
  };

  const handleRescheduleClick = (record) => {
    setRescheduleEventData(record);
    setNewEventDate(toDateInputValue(record.event_details.day_to_meet));
    setNewEventTime(toTimeInputValue(record.event_details.time_to_meet));
    setRescheduleError("");
    setRescheduleShow(true);
  };

  const closeReschedule = () => {
    if (isReschedulingEvent) return;
    setRescheduleShow(false);
    setRescheduleEventData(null);
    setRescheduleError("");
  };

  // why the entered date and time cannot be submitted yet, or "" if they can
  const getRescheduleProblem = () => {
    if (!rescheduleEventData) return "";
    if (!newEventDate || !newEventTime) {
      return "Enter both a new date and a new time.";
    }
    if (
      newEventDate === toDateInputValue(rescheduleEventData.event_details.day_to_meet) &&
      newEventTime === toTimeInputValue(rescheduleEventData.event_details.time_to_meet)
    ) {
      return RESCHEDULE_UNCHANGED;
    }
    const start = new Date(`${newEventDate}T${newEventTime}`);
    if (Number.isNaN(start.getTime())) return "That date or time is not valid.";
    if (start < new Date()) return "The new date and time are in the past.";
    return "";
  };

  const confirmReschedule = () => {
    if (!rescheduleEventData || isReschedulingEvent || getRescheduleProblem()) {
      return;
    }
    const record = rescheduleEventData;

    setIsReschedulingEvent(true);
    setRescheduleError("");
    rescheduleTraining(
      record.event_details.eventID,
      record.event_details.source,
      newEventDate,
      newEventTime,
      cookies["token"],
    )
      .then((res) => {
        setIsReschedulingEvent(false);
        if (!res.data.success) {
          setRescheduleError(
            res.data.type === "reschedule_conflict"
              ? `${res.data.message}. Someone involved already has something booked then.`
              : res.data.message || "Could not reschedule this event.",
          );
          return;
        }
        setRescheduleShow(false);
        setRescheduleEventData(null);
        setModalHeader(
          isNoShowEvent(record) ? "Second Attempt Booked" : "Event Rescheduled",
        );
        setModalBody(
          <div>
            <p>
              {record.event_details.training} moved from {res.data.old_day} at{" "}
              {res.data.old_time} to {res.data.new_day} at {res.data.new_time}.
            </p>
            <p>
              {res.data.cohort_size > 1
                ? `All ${res.data.cohort_size} people on that slot have been emailed.`
                : "Everyone involved has been emailed."}
            </p>
            {isNoShowEvent(record) && (
              <p>
                The no show has been lifted, and the missed date is kept in
                Event Details.
              </p>
            )}
          </div>,
        );
        setShowModalClose(true);
        setModalShow(true);
        fetchAndCacheAllEvents();
      })
      .catch((err) => {
        console.error("Error rescheduling event:", err);
        setIsReschedulingEvent(false);
        setRescheduleError(
          err.response?.data?.message || err.message || "Unknown error",
        );
      });
  };

  const ConfirmedTrainingRow = (props) => {
    const record = props.record;
    const cancelled = isCancelledEvent(record);
    const noShow = isNoShowEvent(record);
    const settled = isSettledEvent(record);
    const past = hasStarted(record.event_details);

    let noShowTitle =
      "Mark the participant as a no show and email everyone involved";
    if (noShow) noShowTitle = "This event is already marked as a no show";
    else if (cancelled) noShowTitle = "This event is cancelled";
    else if (!past) noShowTitle = "This event has not started yet";

    return (
      <tr
        key={record.trainer.name + record.event_details.day_to_meet}
        style={settled ? { opacity: 0.55 } : undefined}
      >
        <td>
          {record.event_details.training}{" "}
          {getEventTypeBadge(record.event_details.event_type)}
        </td>
        <td>{record.trainer.name}</td>
        <td>
          {record.event_details.day_to_meet}
          {cancelled && (
            <>
              {" "}
              <Badge bg="secondary">Cancelled</Badge>
            </>
          )}
          {noShow && (
            <>
              {" "}
              <Badge bg="dark">No show</Badge>
            </>
          )}
        </td>
        <td>{record.event_details.time_to_meet}</td>
        <td>
          <div>
            <Button
              variant="primary"
              size="sm"
              onClick={() => infoConfirmedTraining(record)}
            >
              <IoInformationCircleOutline />
            </Button>
          </div>
        </td>
        <td>
          <div>
            <Button
              variant="primary"
              size="sm"
              onClick={() => attachToTraining(record)}
            >
              <IoAddCircle />
            </Button>
          </div>
        </td>
        <td>
          <div
            title={
              cancelled
                ? "This event is cancelled"
                : noShow
                  ? "Book the second attempt by moving this event to a new date and time"
                  : "Move this event to a new date and time and email everyone involved"
            }
          >
            <Button
              variant="primary"
              size="sm"
              disabled={cancelled}
              onClick={() => handleRescheduleClick(record)}
            >
              <IoTime />
            </Button>
          </div>
        </td>
        <td>
          <div
            title={
              cancelled
                ? "This event is already cancelled"
                : noShow
                  ? "This event is marked as a no show"
                  : "Cancel this event and email everyone involved"
            }
          >
            <Button
              variant="warning"
              size="sm"
              disabled={settled}
              onClick={() => handleCancelClick(record)}
            >
              <IoCloseCircle />
            </Button>
          </div>
        </td>
        <td>
          <div title={noShowTitle}>
            <Button
              variant="dark"
              size="sm"
              disabled={settled || !past}
              onClick={() => handleNoShowClick(record)}
            >
              <IoPersonRemove />
            </Button>
          </div>
        </td>
        {userData && userData.is_admin && (
          <>
            <td>
              <div>
                <Button
                  variant="success"
                  size="sm"
                  onClick={() => handleAddParticipantClick(record)}
                  title="Add participant to this event"
                >
                  <IoAddCircle /> Add
                </Button>
              </div>
            </td>
            <td>
              <div>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => handleDeleteClick(record)}
                  title="Permanently delete this event"
                >
                  <IoTrash />
                </Button>
              </div>
            </td>
          </>
        )}
      </tr>
    );
  };

  const handleChangeConfirmedTraining = useCallback(
    (page) => {
      // Check if we need to load more events
      // 10 items per page, so page needs: (page * 10) items
      const itemsNeeded = page * 10;
      const itemsLoaded = allEventsCache.length;

      if (itemsNeeded > itemsLoaded && hasMoreEvents) {
        // Need to load more data before navigating to this page
        loadMoreEvents();
      }

      setConfirmedTrainingCurPage(page);
    },
    [allEventsCache.length, hasMoreEvents, loadMoreEvents],
  );

  function MyPagination({ total, current, onChangePage }) {
    let items = [];

    if (current > 1) {
      items.push(
        <Pagination.Prev
          key="prev"
          onClick={() => onChangePage(current - 1)}
        />,
      );
    }

    if (total < 10) {
      for (let page = 1; page <= total; page++) {
        items.push(
          <Pagination.Item
            key={page}
            data-page={page}
            active={page === current}
            onClick={() => onChangePage(page)}
          >
            {page}
          </Pagination.Item>,
        );
      }
    } else {
      if (current <= 3) {
        for (let page = 1; page <= 5; page++) {
          items.push(
            <Pagination.Item
              key={page}
              data-page={page}
              active={page === current}
              onClick={() => onChangePage(page)}
            >
              {page}
            </Pagination.Item>,
          );
        }

        items.push(<Pagination.Ellipsis />);
      } else if (current >= total - 2) {
        <Pagination.Ellipsis />;
        for (let page = total - 4; page <= total; page++) {
          items.push(
            <Pagination.Item
              key={page}
              data-page={page}
              active={page === current}
              onClick={() => onChangePage(page)}
            >
              {page}
            </Pagination.Item>,
          );
        }
      } else {
        items.push(<Pagination.Ellipsis />);

        for (let page = current - 2; page <= current + 2; page++) {
          items.push(
            <Pagination.Item
              key={page}
              data-page={page}
              active={page === current}
              onClick={() => onChangePage(page)}
            >
              {page}
            </Pagination.Item>,
          );
        }

        items.push(<Pagination.Ellipsis />);
      }
    }

    if (current < total) {
      items.push(
        <Pagination.Next
          key="next"
          onClick={() => onChangePage(current + 1)}
        />,
      );
    }

    return <Pagination>{items}</Pagination>;
  }

  const handleConfirmedTrainingSort = (info) => {
    const changes = structuredClone(confirmedTrainingFilters);
    const idx = changes.indexOf(info);

    if (idx > -1) {
      // Remove if already selected
      changes.splice(idx, 1);
    } else {
      // Add if not selected
      changes.push(info);
    }

    setConfirmedTrainingFilters(changes);
    setConfirmedTrainingCurPage(1); // Reset to first page when filtering changes
  };

  const extractTrainings = (items) => {
    // For simple array of event types, just return as is
    return items;
  };

  return (
    <div>
      <NavBar />
      {
        <Container className={styles.pageContainer}>
          <Container>
            <h1>Existing Events Page</h1>
          </Container>

          <Container className={styles.sectionContainer}>
            {isRefreshingCache &&
              allEventsCache.length === 0 &&
              reauthorized && (
                <div style={{ textAlign: "center", padding: "40px" }}>
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                  <p style={{ marginTop: "15px", color: "#666" }}>
                    Loading events...
                  </p>
                </div>
              )}

            {reauthorized && allEventsCache.length > 0 && (
              <div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "15px",
                  }}
                >
                  <h4 style={{ margin: 0 }}>Final Confirmed Trainings</h4>
                  <div>
                    <small style={{ marginRight: "10px", color: "#666" }}>
                      Last updated:{" "}
                      {lastRefreshTime
                        ? lastRefreshTime.toLocaleTimeString()
                        : "Loading..."}
                    </small>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => fetchAndCacheAllEvents()}
                      disabled={isRefreshingCache}
                    >
                      {isRefreshingCache ? "Refreshing..." : "Refresh"}
                    </Button>
                  </div>
                </div>
                <Table
                  columns={
                    userData && userData.is_admin
                      ? [
                          "Event type",
                          "Trainer Name",
                          "Day to Meet",
                          "Time to Meet (in CST)",
                          "Event Info",
                          "Join Event",
                          "Reschedule",
                          "Cancel Event",
                          "No Show",
                          "Add Participant",
                          "Delete",
                        ]
                      : [
                          "Event type",
                          "Trainer Name",
                          "Day to Meet",
                          "Time to Meet (in CST)",
                          "Event Info",
                          "Join Event",
                          "Reschedule",
                          "Cancel Event",
                          "No Show",
                        ]
                  }
                  sortable={
                    userData && userData.is_admin
                      ? [
                          true,
                          false,
                          false,
                          false,
                          false,
                          false,
                          false,
                          false,
                          false,
                          false,
                          false,
                        ]
                      : [
                          true,
                          false,
                          false,
                          false,
                          false,
                          false,
                          false,
                          false,
                          false,
                        ]
                  }
                  handleSorted={
                    userData && userData.is_admin
                      ? [
                          handleConfirmedTrainingSort,
                          false,
                          false,
                          false,
                          false,
                          false,
                          false,
                          false,
                          false,
                          false,
                          false,
                        ]
                      : [
                          handleConfirmedTrainingSort,
                          false,
                          false,
                          false,
                          false,
                          false,
                          false,
                          false,
                          false,
                        ]
                  }
                  filters={confirmedTrainingFilters}
                  all={extractTrainings(["Exam", "Training", "Mock"])}
                  rows={confirmedTraining.map((record) => (
                    <ConfirmedTrainingRow
                      record={record}
                      key={"confirmedTraining" + JSON.stringify(record)}
                    />
                  ))}
                />
                {confirmedTrainingTotalPage > 1 && (
                  <MyPagination
                    total={confirmedTrainingTotalPage}
                    current={confirmedtrainingCurPage}
                    onChangePage={handleChangeConfirmedTraining}
                  />
                )}
                {isFetchingMore && (
                  <div style={{ textAlign: "center", padding: "15px" }}>
                    <Spinner
                      animation="border"
                      size="sm"
                      role="status"
                      style={{ marginRight: "10px" }}
                    />
                    <span style={{ color: "#666" }}>
                      Loading more events...
                    </span>
                  </div>
                )}
                {hasMoreEvents && !isFetchingMore && (
                  <div style={{ textAlign: "center", padding: "15px" }}>
                    <Button
                      variant="outline-primary"
                      onClick={() => loadMoreEvents()}
                      disabled={isFetchingMore}
                    >
                      Load More Events
                    </Button>
                  </div>
                )}
              </div>
            )}
          </Container>
        </Container>
      }
      <Modal
        show={modalShow}
        onHide={() => {
          setModalShow(false);
          setInfoToUse([]);
          setShowModalClose(false);
        }}
        backdrop={undefined}
      >
        <Modal.Header closeButton={true}>
          <Modal.Title>{modalHeader}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{modalBody}</Modal.Body>
        {showModalClose && (
          <Modal.Footer>
            <Button variant="primary" onClick={handleSendConfirmedTraining}>
              {modalBtnText}
            </Button>
          </Modal.Footer>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={deleteConfirmShow}
        onHide={() => setDeleteConfirmShow(false)}
        backdrop={undefined}
      >
        <Modal.Header closeButton={true}>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to permanently delete this event?</p>
          <p>
            <strong>{deleteEventData?.event_details.training}</strong> on{" "}
            <strong>{deleteEventData?.event_details.day_to_meet}</strong> at{" "}
            <strong>{deleteEventData?.event_details.time_to_meet}</strong>
          </p>
          <p className="text-danger">This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setDeleteConfirmShow(false)}
            disabled={isDeletingEvent}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={confirmDelete}
            disabled={isDeletingEvent}
          >
            {isDeletingEvent ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Deleting...
              </>
            ) : (
              "Delete Permanently"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Cancel Event Modal */}
      <Modal
        show={cancelConfirmShow}
        onHide={closeCancel}
        backdrop={isCancellingEvent ? "static" : true}
      >
        <Modal.Header closeButton={!isCancellingEvent}>
          <Modal.Title>Cancel Event</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {cancelEventData && (
            <Container>
              <p>
                Cancel <b>{cancelEventData.event_details.training}</b> with{" "}
                <b>{cancelEventData.trainer.name}</b> on{" "}
                <b>
                  {cancelEventData.event_details.day_to_meet} at{" "}
                  {cancelEventData.event_details.time_to_meet}
                </b>
                ?
              </p>
              <p>
                This emails the trainee and the trainer, and the model RA on a
                mock. The scheduler for this training type is copied. The event
                stays on this page, marked Cancelled, and the slot is freed for
                both people.
              </p>
              <p style={{ color: "#a00" }}>
                Use Delete for test entries and mistakes. That removes the
                record and emails nobody.
              </p>
              {cancelError && <p style={{ color: "#a00" }}>{cancelError}</p>}
            </Container>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeCancel}
            disabled={isCancellingEvent}
          >
            Keep event
          </Button>
          <Button
            variant="warning"
            onClick={confirmCancel}
            disabled={isCancellingEvent}
          >
            {isCancellingEvent ? "Sending emails..." : "Cancel event and email everyone"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* No Show Modal */}
      <Modal
        show={noShowConfirmShow}
        onHide={closeNoShow}
        backdrop={isMarkingNoShow ? "static" : true}
      >
        <Modal.Header closeButton={!isMarkingNoShow}>
          <Modal.Title>Mark as No Show</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {noShowEventData && (
            <Container>
              <p>
                Did <b>{noShowEventData.trainee?.name || "the participant"}</b>{" "}
                miss <b>{noShowEventData.event_details.training}</b> with{" "}
                <b>{noShowEventData.trainer.name}</b> on{" "}
                <b>
                  {noShowEventData.event_details.day_to_meet} at{" "}
                  {noShowEventData.event_details.time_to_meet}
                </b>
                ?
              </p>
              <p>
                This emails the trainee and the trainer, and the model RA on a
                mock. The scheduler for this training type is copied. The event
                stays on this page, marked No show, and rescheduling it later
                books a second attempt.
              </p>
              <p>
                Only this person is marked. Anyone else sharing the slot is
                left as they are.
              </p>
              {noShowError && <p style={{ color: "#a00" }}>{noShowError}</p>}
            </Container>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeNoShow}
            disabled={isMarkingNoShow}
          >
            They were there
          </Button>
          <Button
            variant="dark"
            onClick={confirmNoShow}
            disabled={isMarkingNoShow}
          >
            {isMarkingNoShow
              ? "Sending emails..."
              : "Mark no show and email everyone"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Reschedule Modal */}
      <Modal
        show={rescheduleShow}
        onHide={closeReschedule}
        backdrop={isReschedulingEvent ? "static" : true}
      >
        <Modal.Header closeButton={!isReschedulingEvent}>
          <Modal.Title>Reschedule Event</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {rescheduleEventData && (
            <Container>
              <p>
                <b>{rescheduleEventData.event_details.training}</b> with{" "}
                <b>{rescheduleEventData.trainer.name}</b>
                <br />
                Currently {rescheduleEventData.event_details.day_to_meet} at{" "}
                {rescheduleEventData.event_details.time_to_meet} (CST)
              </p>
              <Form onSubmit={(e) => e.preventDefault()}>
                <Form.Group className="mb-3" controlId="rescheduleEventDate">
                  <Form.Label>New date</Form.Label>
                  <Form.Control
                    type="date"
                    value={newEventDate}
                    onChange={(e) => setNewEventDate(e.target.value)}
                    disabled={isReschedulingEvent}
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3" controlId="rescheduleEventTime">
                  <Form.Label>New time (CST)</Form.Label>
                  <Form.Control
                    type="time"
                    value={newEventTime}
                    onChange={(e) => setNewEventTime(e.target.value)}
                    disabled={isReschedulingEvent}
                    required
                  />
                </Form.Group>
              </Form>
              {getRescheduleProblem() && (
                <p
                  style={{
                    color:
                      getRescheduleProblem() === RESCHEDULE_UNCHANGED
                        ? "#666"
                        : "#a00",
                  }}
                >
                  {getRescheduleProblem()}
                </p>
              )}
              <p>
                Everyone sharing this slot with this trainer moves together,
                and each of them is emailed the new time, with the scheduler
                for this training type copied. A clash with something already
                booked blocks the move.
              </p>
              {rescheduleError && (
                <p style={{ color: "#a00" }}>{rescheduleError}</p>
              )}
            </Container>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeReschedule}
            disabled={isReschedulingEvent}
          >
            Keep current time
          </Button>
          <Button
            variant="primary"
            onClick={confirmReschedule}
            disabled={isReschedulingEvent || Boolean(getRescheduleProblem())}
          >
            {isReschedulingEvent
              ? "Sending emails..."
              : "Reschedule and email everyone"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add Participant Modal */}
      <Modal
        show={addParticipantShow}
        onHide={() => setAddParticipantShow(false)}
        backdrop={undefined}
        size="lg"
      >
        <Modal.Header closeButton={true}>
          <Modal.Title>Add Participant to Event</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedEventForAdd && (
            <div className="mb-3">
              <p>
                <strong>Event:</strong>{" "}
                {selectedEventForAdd.event_details.training}
              </p>
              <p>
                <strong>Date:</strong>{" "}
                {selectedEventForAdd.event_details.day_to_meet} at{" "}
                {selectedEventForAdd.event_details.time_to_meet}
              </p>
            </div>
          )}

          <div className="mb-3">
            <label className="form-label">
              Search for Participant (name or email):
            </label>
            <input
              type="text"
              className="form-control"
              placeholder="Enter name or email..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </div>

          {searchResults.length > 0 && (
            <div className="mb-3">
              <label className="form-label">Results:</label>
              <div
                style={{
                  maxHeight: "250px",
                  overflowY: "auto",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                }}
              >
                {searchResults.map((user, idx) => (
                  <div
                    key={idx}
                    style={{
                      padding: "10px",
                      cursor: "pointer",
                      backgroundColor:
                        selectedUser?.email === user.email
                          ? "#e3f2fd"
                          : "white",
                      borderBottom: "1px solid #eee",
                    }}
                    onClick={() => setSelectedUser(user)}
                  >
                    <strong>{user.name}</strong>
                    <br />
                    <small>{user.email}</small>
                  </div>
                ))}
              </div>
            </div>
          )}

          {selectedUser && (
            <div className="alert alert-info">
              <strong>Selected:</strong> {selectedUser.name} (
              {selectedUser.email})
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setAddParticipantShow(false)}
            disabled={addParticipantLoading}
          >
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={confirmAddParticipant}
            disabled={!selectedUser || addParticipantLoading}
          >
            {addParticipantLoading ? (
              <Spinner animation="border" size="sm" className="me-2" />
            ) : (
              ""
            )}
            Add Participant
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ExistingTrainingPage;
